import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, ArrowLeft, Sparkles, FileText, Eye, Send, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { absoluteMediaUrl } from "@/components/ImageField";

const STATUS_LABELS = {
  bozza: "Bozza", da_approvare: "Da approvare", inviato: "Inviato",
  accettato: "Accettato", rifiutato: "Rifiutato", scaduto: "Scaduto",
};
const STATUS_COLORS = {
  bozza: "text-taupe border-hair", da_approvare: "text-gold border-gold/50",
  inviato: "text-blue-700 border-blue-700/40", accettato: "text-emerald-700 border-emerald-700/40",
  rifiutato: "text-red-700 border-red-700/40", scaduto: "text-taupe border-hair",
};

const today = () => new Date().toISOString().slice(0, 10);

const emptyQuote = {
  number: "", date: today(), validity: "", client_name: "", client_email: "", client_phone: "",
  client_address: "", subject: "", property_desc: "", property_location: "", area_sqm: "",
  service_interest: "", needs_description: "", included_text: "", excluded_text: "",
  phases_text: "", timing_text: "", fees_text: "", expenses_text: "", advances_text: "",
  payment_terms: "", revisions: "", conditions: "", notes: "", attachment_url: "",
  items: [], discount_type: "percent", discount_value: 0, fiscal_note: "",
  status: "bozza", ai_generated: false,
};

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

function F({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="eyebrow !tracking-[0.14em]">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

// Bozza manuale: organizza solo i dati inseriti, segnala i mancanti. Mai dati inventati.
function buildManualDraft(q) {
  const missing = [];
  const placeholder = (v, label) => (v && String(v).trim() ? v : (missing.push(label), `[DA COMPLETARE: ${label}]`));
  const bullets = (t) => t.split("\n").map((r) => r.trim()).filter(Boolean).map((r) => `• ${r.replace(/^[•\-*]\s*/, "")}`).join("\n");
  const fields = {};
  fields.subject = q.subject || `Progetto di interior design — ${placeholder(q.property_desc, "immobile")} a ${placeholder(q.property_location, "località")}${q.area_sqm ? ` (~${q.area_sqm} mq)` : ""}`;
  fields.included_text = q.included_text ? bullets(q.included_text) : `[DA COMPLETARE: prestazioni incluse]`;
  if (!q.included_text) missing.push("prestazioni incluse");
  fields.excluded_text = q.excluded_text ? bullets(q.excluded_text) : "";
  fields.phases_text = q.phases_text ? bullets(q.phases_text) : `[DA COMPLETARE: fasi di lavoro]`;
  if (!q.phases_text) missing.push("fasi di lavoro");
  fields.timing_text = q.timing_text || `[DA COMPLETARE: tempi indicativi]`;
  if (!q.timing_text) missing.push("tempi indicativi");
  fields.fees_text = q.fees_text || "";
  fields.payment_terms = q.payment_terms || `[DA COMPLETARE: modalità e scadenze di pagamento]`;
  if (!q.payment_terms) missing.push("modalità di pagamento");
  fields.conditions = q.conditions || "La presente è una bozza di preventivo non vincolante, soggetta a verifica e approvazione finale dello studio.";
  fields.notes = q.notes || "";
  if (!q.client_name) missing.push("nome cliente");
  if (!q.client_email) missing.push("email cliente");
  if (!q.validity) missing.push("validità dell'offerta");
  return { fields, missing };
}

export function QuotesPanel() {
  const [quotes, setQuotes] = useState([]);
  const [services, setServices] = useState([]);
  const [aiStatus, setAiStatus] = useState({ enabled: false });
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [missing, setMissing] = useState([]);
  const [sendFor, setSendFor] = useState(null);
  const [sendForm, setSendForm] = useState({ to: "", subject: "", body: "" });
  const [sending, setSending] = useState(false);

  const load = () => api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => toast.error("Errore caricamento preventivi"));
  useEffect(() => {
    load();
    api.get("/admin/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/admin/quotes/ai-status").then((r) => setAiStatus(r.data)).catch(() => {});
  }, []);

  const totals = useMemo(() => {
    if (!editing) return { subtotal: 0, discount: 0, total: 0 };
    const subtotal = editing.items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0);
    const dv = parseFloat(editing.discount_value) || 0;
    const discount = editing.discount_type === "percent" ? (subtotal * dv) / 100 : dv;
    return { subtotal, discount: Math.min(discount, subtotal), total: Math.max(0, subtotal - Math.min(discount, subtotal)) };
  }, [editing]);

  const set = (k) => (e) => setEditing((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const setItem = (i, k, v) => setEditing((p) => ({ ...p, items: p.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) }));

  const save = async () => {
    if (!editing.client_name?.trim()) { toast.error("Il nome del cliente è obbligatorio"); return; }
    setBusy(true);
    try {
      if (isNew) {
        const { data } = await api.post("/admin/quotes", editing);
        toast.success(`Preventivo ${data.number} creato`);
        setEditing({ ...emptyQuote, ...data }); setIsNew(false);
      } else {
        const { data } = await api.put(`/admin/quotes/${editing.id}`, editing);
        setEditing({ ...emptyQuote, ...data });
        toast.success("Preventivo salvato");
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally { setBusy(false); }
  };

  const generateDraft = async () => {
    setAiBusy(true); setMissing([]);
    try {
      if (!aiStatus.enabled) {
        const { fields, missing: m } = buildManualDraft(editing);
        setEditing((p) => ({ ...p, ...Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, p[k] || v])) }));
        setMissing(m);
        toast.success("Bozza generata in modalità manuale (AI non configurata)");
        return;
      }
      const { data } = await api.post("/admin/quotes/ai-draft", editing);
      setEditing((p) => ({ ...p, ...Object.fromEntries(Object.entries(data.fields).map(([k, v]) => [k, p[k] || v])), ai_generated: true }));
      setMissing(data.missing || []);
      toast.success("Bozza AI generata — rivedila prima di salvare");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore AI");
    } finally { setAiBusy(false); }
  };

  const generatePdf = async () => {
    if (isNew || !editing.id) { toast.error("Salva prima il preventivo"); return; }
    setPdfBusy(true);
    try {
      await save();
      const { data } = await api.post(`/admin/quotes/${editing.id}/pdf`);
      setEditing((p) => ({ ...p, pdf_url: data.pdf_url }));
      toast.success("PDF generato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore generazione PDF");
    } finally { setPdfBusy(false); }
  };

  const openSend = (q) => {
    setSendFor(q);
    setSendForm({
      to: q.client_email || "",
      subject: `Preventivo n. ${q.number} — ${q.subject || "Progetto di interior design"}`,
      body: `Gentile ${q.client_name || "Cliente"},\n\nin allegato trova il preventivo n. ${q.number} relativo a ${q.subject || "il progetto concordato"}.\n\nResto a disposizione per qualsiasi chiarimento.\n\nGiovanna Matino`,
    });
  };

  const doSend = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/quotes/${sendFor.id}/send`, sendForm);
      if (data.success) { toast.success("Preventivo inviato"); setSendFor(null); load(); if (editing?.id === sendFor.id) setEditing((p) => ({ ...p, status: "inviato" })); }
      else toast.error(data.message || "Invio non riuscito");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio");
    } finally { setSending(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo preventivo?")) return;
    await api.delete(`/admin/quotes/${id}`);
    toast.success("Preventivo eliminato");
    setEditing(null); load();
  };

  /* ---------- Invio modal ---------- */
  const sendModal = sendFor && (
    <div className="fixed inset-0 z-[80] bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-lenis-prevent data-testid="quote-send-modal" onClick={() => setSendFor(null)}>
      <div className="relative bg-bone w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setSendFor(null)} className="absolute top-3 right-3 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-gold" data-testid="quote-send-close" aria-label="Chiudi"><X size={18} /></button>
        <div className="p-8 space-y-5">
          <div>
            <div className="eyebrow">Invio controllato</div>
            <h3 className="font-serif text-3xl mt-2">Prepara invio — Preventivo n. {sendFor.number}</h3>
            <p className="text-xs text-taupe mt-2">Nessun invio automatico: il preventivo parte solo con il tuo clic su "Invia preventivo".</p>
          </div>
          <F label="Destinatario" testId="quote-send-to-field">
            <Input type="email" value={sendForm.to} onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })} data-testid="quote-send-to" className="rounded-none" />
          </F>
          <F label="Oggetto dell'email" testId="quote-send-subject-field">
            <Input value={sendForm.subject} onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })} data-testid="quote-send-subject" className="rounded-none" />
          </F>
          <F label="Testo dell'email" testId="quote-send-body-field">
            <Textarea rows={6} value={sendForm.body} onChange={(e) => setSendForm({ ...sendForm, body: e.target.value })} data-testid="quote-send-body" className="rounded-none" />
          </F>
          <div className="text-xs text-taupe border border-hair p-3" data-testid="quote-send-attachment">
            Allegato PDF: {sendFor.pdf_url ? <a href={absoluteMediaUrl(sendFor.pdf_url)} target="_blank" rel="noreferrer" className="text-gold underline break-all">preventivo {sendFor.number}.pdf</a> : <span className="text-red-700">nessun PDF generato — genera il PDF prima dell'invio</span>}
          </div>
          <button onClick={doSend} disabled={sending || !sendFor.pdf_url || !sendForm.to} className="btn-ink w-full min-h-[48px] disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="quote-send-confirm">
            <Send size={15} /> {sending ? "Invio in corso…" : "Invia preventivo"}
          </button>
        </div>
      </div>
    </div>
  );

  /* ---------- Editor ---------- */
  if (editing) {
    return (
      <div data-testid="quote-editor">
        {sendModal}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <button onClick={() => { setEditing(null); setMissing([]); }} className="inline-flex items-center gap-2 min-h-[44px] text-sm text-taupe hover:text-ink" data-testid="quote-editor-back">
            <ArrowLeft size={15} /> Tutti i preventivi
          </button>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <button onClick={generateDraft} disabled={aiBusy} className="flex-1 sm:flex-none min-h-[44px] inline-flex items-center justify-center gap-2 text-xs tracking-[0.12em] uppercase border border-gold text-gold px-4 py-2 hover:bg-gold hover:text-bone transition-colors disabled:opacity-60" data-testid="quote-ai-draft">
              <Sparkles size={14} /> {aiBusy ? "Generazione…" : "Genera bozza con AI"}
            </button>
            <button onClick={generatePdf} disabled={pdfBusy} className="flex-1 sm:flex-none min-h-[44px] inline-flex items-center justify-center gap-2 text-xs tracking-[0.12em] uppercase border border-ink px-4 py-2 hover:bg-ink hover:text-bone transition-colors disabled:opacity-60" data-testid="quote-generate-pdf">
              <FileText size={14} /> {pdfBusy ? "Generazione…" : editing.pdf_url ? "Rigenera PDF" : "Genera PDF"}
            </button>
            {editing.pdf_url && (
              <a href={absoluteMediaUrl(editing.pdf_url)} target="_blank" rel="noreferrer" className="flex-1 sm:flex-none min-h-[44px] inline-flex items-center justify-center gap-2 text-xs tracking-[0.12em] uppercase border border-hair text-taupe px-4 py-2 hover:border-ink hover:text-ink transition-colors" data-testid="quote-preview-pdf">
                <Eye size={14} /> Anteprima
              </a>
            )}
            <button onClick={save} disabled={busy} className="btn-ink flex-1 sm:flex-none min-h-[44px] disabled:opacity-60" data-testid="quote-save">
              {busy ? "Salvataggio…" : "Salva bozza"}
            </button>
          </div>
        </div>

        {!aiStatus.enabled && (
          <p className="text-xs text-taupe border border-hair bg-white p-3 mb-6" data-testid="quote-ai-manual-note">
            AI non configurata: il pulsante "Genera bozza" lavora in modalità manuale (organizza solo i dati che hai inserito, senza inventare nulla). L'attivazione dell'AI avviene tramite variabili d'ambiente e può comportare costi del provider.
          </p>
        )}
        {missing.length > 0 && (
          <div className="text-xs border border-gold/50 bg-gold/5 p-3 mb-6" data-testid="quote-missing">
            <span className="eyebrow !text-[10px]">Dati mancanti da completare:</span>
            <div className="mt-1 text-taupe">{missing.join(" · ")}</div>
          </div>
        )}

        <div className="space-y-6">
          <section className="bg-white border border-hair p-5 md:p-8">
            <div className="eyebrow mb-6">Documento</div>
            <div className="grid sm:grid-cols-3 gap-5">
              <F label="Numero preventivo" testId="quote-f-number"><Input value={editing.number} onChange={set("number")} placeholder="automatico" className="rounded-none" data-testid="quote-number" /></F>
              <F label="Data" testId="quote-f-date"><Input type="date" value={editing.date} onChange={set("date")} className="rounded-none" data-testid="quote-date" /></F>
              <F label="Validità offerta" testId="quote-f-validity"><Input value={editing.validity} onChange={set("validity")} placeholder="es. 30 giorni" className="rounded-none" data-testid="quote-validity" /></F>
            </div>
          </section>

          <section className="bg-white border border-hair p-5 md:p-8">
            <div className="eyebrow mb-6">Cliente</div>
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Nome e cognome / Ragione sociale *" testId="quote-f-client"><Input value={editing.client_name} onChange={set("client_name")} className="rounded-none" data-testid="quote-client-name" /></F>
              <F label="Email" testId="quote-f-email"><Input type="email" value={editing.client_email} onChange={set("client_email")} className="rounded-none" data-testid="quote-client-email" /></F>
              <F label="Telefono" testId="quote-f-phone"><Input value={editing.client_phone} onChange={set("client_phone")} className="rounded-none" data-testid="quote-client-phone" /></F>
              <F label="Indirizzo" testId="quote-f-address"><Input value={editing.client_address} onChange={set("client_address")} className="rounded-none" data-testid="quote-client-address" /></F>
            </div>
          </section>

          <section className="bg-white border border-hair p-5 md:p-8">
            <div className="eyebrow mb-6">Incarico</div>
            <div className="grid gap-5">
              <F label="Oggetto dell'incarico" testId="quote-f-subject"><Input value={editing.subject} onChange={set("subject")} className="rounded-none" data-testid="quote-subject" /></F>
              <div className="grid sm:grid-cols-3 gap-5">
                <F label="Immobile" testId="quote-f-property"><Input value={editing.property_desc} onChange={set("property_desc")} placeholder="es. Appartamento" className="rounded-none" data-testid="quote-property" /></F>
                <F label="Località" testId="quote-f-location"><Input value={editing.property_location} onChange={set("property_location")} className="rounded-none" data-testid="quote-location" /></F>
                <F label="Metratura indicativa" testId="quote-f-area"><Input value={editing.area_sqm} onChange={set("area_sqm")} placeholder="es. 90" className="rounded-none" data-testid="quote-area" /></F>
              </div>
              <F label="Servizio interessato" testId="quote-f-service">
                <Select value={editing.service_interest} onValueChange={set("service_interest")}>
                  <SelectTrigger className="rounded-none" data-testid="quote-service"><SelectValue placeholder="Seleziona servizio" /></SelectTrigger>
                  <SelectContent>
                    {services.map((s) => <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Descrizione delle esigenze" testId="quote-f-needs"><Textarea rows={3} value={editing.needs_description} onChange={set("needs_description")} className="rounded-none" data-testid="quote-needs" /></F>
            </div>
          </section>

          <section className="bg-white border border-hair p-5 md:p-8">
            <div className="eyebrow mb-6">Contenuti del preventivo</div>
            <div className="grid gap-5">
              <F label="Prestazioni incluse" testId="quote-f-included"><Textarea rows={4} value={editing.included_text} onChange={set("included_text")} className="rounded-none" data-testid="quote-included" /></F>
              <F label="Prestazioni escluse" testId="quote-f-excluded"><Textarea rows={3} value={editing.excluded_text} onChange={set("excluded_text")} className="rounded-none" data-testid="quote-excluded" /></F>
              <div className="grid sm:grid-cols-2 gap-5">
                <F label="Fasi di lavoro" testId="quote-f-phases"><Textarea rows={3} value={editing.phases_text} onChange={set("phases_text")} className="rounded-none" data-testid="quote-phases" /></F>
                <F label="Tempi indicativi" testId="quote-f-timing"><Textarea rows={3} value={editing.timing_text} onChange={set("timing_text")} className="rounded-none" data-testid="quote-timing" /></F>
              </div>
              <F label="Modalità e scadenze di pagamento" testId="quote-f-payment"><Textarea rows={2} value={editing.payment_terms} onChange={set("payment_terms")} className="rounded-none" data-testid="quote-payment" /></F>
              <div className="grid sm:grid-cols-2 gap-5">
                <F label="Revisioni comprese" testId="quote-f-revisions"><Input value={editing.revisions} onChange={set("revisions")} placeholder="es. 2" className="rounded-none" data-testid="quote-revisions" /></F>
                <F label="Allegato (URL file caricato)" testId="quote-f-attachment"><Input value={editing.attachment_url} onChange={set("attachment_url")} placeholder="opzionale" className="rounded-none" data-testid="quote-attachment" /></F>
              </div>
              <F label="Condizioni" testId="quote-f-conditions"><Textarea rows={3} value={editing.conditions} onChange={set("conditions")} className="rounded-none" data-testid="quote-conditions" /></F>
              <F label="Note" testId="quote-f-notes"><Textarea rows={2} value={editing.notes} onChange={set("notes")} className="rounded-none" data-testid="quote-notes" /></F>
            </div>
          </section>

          <section className="bg-white border border-hair p-5 md:p-8" data-testid="quote-items-section">
            <div className="eyebrow mb-6">Corrispettivi</div>
            <F label="Compenso — testo descrittivo" testId="quote-f-fees"><Textarea rows={2} value={editing.fees_text} onChange={set("fees_text")} className="rounded-none mb-6" data-testid="quote-fees" /></F>

            <div className="space-y-3">
              {editing.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end border border-hair p-3" data-testid={`quote-item-${i}`}>
                  <div className="col-span-12 sm:col-span-5">
                    <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Descrizione</Label>
                    <Input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} className="rounded-none mt-1" data-testid={`quote-item-desc-${i}`} />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Qtà</Label>
                    <Input type="number" step="0.5" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="rounded-none mt-1" data-testid={`quote-item-qty-${i}`} />
                  </div>
                  <div className="col-span-4 sm:col-span-1">
                    <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Unità</Label>
                    <Input value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} className="rounded-none mt-1" data-testid={`quote-item-unit-${i}`} />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Prezzo €</Label>
                    <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className="rounded-none mt-1" data-testid={`quote-item-price-${i}`} />
                  </div>
                  <div className="col-span-10 sm:col-span-1 text-sm font-serif self-center">{(eur((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0)))}</div>
                  <div className="col-span-2 sm:col-span-1 flex justify-end">
                    <button type="button" onClick={() => setEditing((p) => ({ ...p, items: p.items.filter((_, j) => j !== i) }))} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-red-700 hover:text-red-900" data-testid={`quote-item-remove-${i}`} aria-label="Rimuovi voce"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setEditing((p) => ({ ...p, items: [...p.items, { description: "", quantity: 1, unit: "cad", unit_price: 0 }] }))} className="min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase border border-ink px-4 py-2 hover:bg-ink hover:text-bone transition-colors" data-testid="quote-item-add">
                <Plus size={14} /> Aggiungi voce
              </button>
            </div>

            <div className="grid sm:grid-cols-3 gap-5 mt-6">
              <F label="Sconto (facoltativo)" testId="quote-f-discount">
                <div className="flex gap-2">
                  <Input type="number" step="0.01" min="0" value={editing.discount_value} onChange={(e) => setEditing((p) => ({ ...p, discount_value: e.target.value }))} className="rounded-none" data-testid="quote-discount" />
                  <Select value={editing.discount_type} onValueChange={set("discount_type")}>
                    <SelectTrigger className="rounded-none w-24" data-testid="quote-discount-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">%</SelectItem>
                      <SelectItem value="amount">€</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </F>
              <div className="sm:col-span-2">
                <F label="Nota fiscale (inserita manualmente)" testId="quote-f-fiscal">
                  <Input value={editing.fiscal_note} onChange={set("fiscal_note")} placeholder="es. Operazione fuori campo IVA ex art. ..." className="rounded-none" data-testid="quote-fiscal-note" />
                </F>
                <p className="text-[11px] text-taupe mt-2">Nessuna IVA, rivalsa, contributo o ritenuta viene aggiunta automaticamente: il regime fiscale lo imposti tu qui.</p>
              </div>
            </div>

            <div className="mt-6 border-t border-hair pt-4 flex flex-col items-end gap-1 text-sm" data-testid="quote-totals">
              <div className="flex justify-between w-full sm:w-64"><span className="text-taupe">Subtotale</span><span className="font-serif">{eur(totals.subtotal)}</span></div>
              {totals.discount > 0 && <div className="flex justify-between w-full sm:w-64"><span className="text-taupe">Sconto</span><span className="font-serif">- {eur(totals.discount)}</span></div>}
              <div className="flex justify-between w-full sm:w-64 text-lg border-t border-ink pt-2 mt-1"><span>Totale</span><span className="font-serif" data-testid="quote-total">{eur(totals.total)}</span></div>
            </div>
          </section>

          <section className="bg-white border border-hair p-5 md:p-8">
            <div className="grid sm:grid-cols-2 gap-5 items-end">
              <F label="Stato" testId="quote-f-status">
                <Select value={editing.status} onValueChange={set("status")}>
                  <SelectTrigger className="rounded-none" data-testid="quote-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <div className="flex flex-wrap gap-2 sm:justify-end">
                {!isNew && editing.id && (
                  <>
                    <button onClick={() => openSend(editing)} className="min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase border border-ink px-4 py-2 hover:bg-ink hover:text-bone transition-colors" data-testid="quote-prepare-send">
                      <Send size={14} /> Prepara invio
                    </button>
                    <button onClick={() => remove(editing.id)} className="min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase border border-red-700 text-red-700 px-4 py-2 hover:bg-red-700 hover:text-bone transition-colors" data-testid="quote-delete">
                      <Trash2 size={14} /> Elimina
                    </button>
                  </>
                )}
              </div>
            </div>
            {editing.sent_at && (
              <p className="text-xs text-taupe mt-4" data-testid="quote-sent-info">
                Inviato il {new Date(editing.sent_at).toLocaleString("it-IT")} a {editing.sent_to} · Esito: {editing.send_result}
              </p>
            )}
          </section>
        </div>
      </div>
    );
  }

  /* ---------- Lista ---------- */
  return (
    <div data-testid="quotes-panel">
      {sendModal}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-taupe">Bozze di preventivo professionali, anche con assistenza AI. Nulla viene inviato senza la tua approvazione.</p>
        <button onClick={() => { setIsNew(true); setEditing({ ...emptyQuote }); setMissing([]); }} className="btn-ink inline-flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px]" data-testid="quote-new">
          <Plus size={15} /> Nuovo preventivo
        </button>
      </div>
      {quotes.length === 0 && <div className="p-10 text-center text-taupe bg-white border border-hair">Nessun preventivo creato.</div>}
      <div className="grid md:grid-cols-2 gap-4">
        {quotes.map((q) => (
          <div key={q.id} className="bg-white border border-hair p-5" data-testid={`quote-card-${q.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <div className="font-serif text-xl">N. {q.number}</div>
                <div className="text-sm mt-1 break-words">{q.client_name}</div>
                <div className="text-xs text-taupe mt-1">{q.date}{q.subject ? ` · ${q.subject}` : ""}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-serif text-xl">{eur(q.total)}</div>
                <span className={`inline-block mt-1 text-[10px] tracking-[0.14em] uppercase border px-2 py-0.5 ${STATUS_COLORS[q.status] || STATUS_COLORS.bozza}`} data-testid={`quote-status-${q.id}`}>{STATUS_LABELS[q.status] || q.status}</span>
              </div>
            </div>
            {q.sent_at && <div className="text-[11px] text-taupe mt-2">Inviato il {new Date(q.sent_at).toLocaleString("it-IT")} a {q.sent_to}</div>}
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => { setIsNew(false); setEditing({ ...emptyQuote, ...q }); setMissing([]); }} className="min-h-[44px] inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-ink px-3 hover:bg-ink hover:text-bone transition-colors" data-testid={`quote-edit-${q.id}`}><Pencil size={13} /> Modifica</button>
              {q.pdf_url && <a href={absoluteMediaUrl(q.pdf_url)} target="_blank" rel="noreferrer" className="min-h-[44px] inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-hair text-taupe px-3 hover:border-ink hover:text-ink transition-colors" data-testid={`quote-pdf-${q.id}`}><Eye size={13} /> PDF</a>}
              <button onClick={() => openSend(q)} className="min-h-[44px] inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-ink px-3 hover:bg-ink hover:text-bone transition-colors" data-testid={`quote-send-${q.id}`}><Send size={13} /> Invia</button>
              <button onClick={() => remove(q.id)} className="min-h-[44px] inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-red-700 text-red-700 px-3 hover:bg-red-700 hover:text-bone transition-colors" data-testid={`quote-delete-${q.id}`}><Trash2 size={13} /> Elimina</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
